import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Col, Form, Modal, ToggleButton } from 'react-bootstrap';
import { makeBold } from '@common/utils';
import { FormInput, FormSelect, ISelectOption } from '@common/components/forms';
import { PetCustomReminderModel, ReminderSourceModel } from '@common/models';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ReminderDays, ReminderSource } from '@common/models/Enums';
import { DateTime } from 'luxon';
import { useApp, usePetDetailsStore, useUnsavedChangesWarning } from '@common/hooks';
import { YesNoConfirmModal } from '@common/components/modals';
import { SuspenseButton } from '@common/components/utility';
import { useHospitalApp } from '@hospital/hooks';

interface IThirdPartyRemindersDueDateModalProps {
    reminder: PetCustomReminderModel | undefined;
    onCancel: () => void;
    isEditing?: boolean;
    updateReminder?: () => Promise<void>;
    modalVisible: boolean;
}

interface IReminderDaysKeyValuePair {
    displayString: string;
    reminderDays: number;
}

interface IReminderSourceDateLabel {
    reminderSourceId: number;
    label: string;
}

export const ThirdPartyRemindersDueDateModal = observer(({ reminder, onCancel, isEditing, updateReminder, modalVisible }: IThirdPartyRemindersDueDateModalProps) => {
    const store = usePetDetailsStore();
    const { configuration } = useApp();
    const { control, setValue, getValues, formState: { isDirty }, resetField } = useFormContext<PetCustomReminderModel>();
    const [dueDateValue, givenDateValue, isInactive, reminderDeactivationReason, reminderDeactivationNote, reminderSourceIdValue] =
        useWatch(
            {
                control,
                name: [
                    "dueDate",
                    "givenDate",
                    "isInactive",
                    "reminderDeactivationReasonId",
                    "reminderDeactivationNote",
                    "reminderSourceId"
                ]
            })
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedReminderSource, setSelectedReminderSource] = useState<IReminderSourceDateLabel | undefined>(undefined);
    
    const { currentHospital } = useHospitalApp();
	const timeZone = currentHospital?.store?.timezoneName;
    const today = DateTime.local({ zone: timeZone}).toUTC(undefined, { keepLocalTime: true }).startOf('day');

    const previousDueDateRef = useRef<string>('');
    const previousGivenDateRef = useRef<string>('');
    const previousReminderSourceIdRef = useRef<number>(0);
    const previousReminderNameRef = useRef<string>('');
    const previousReminderInactive = useRef<boolean>(false);
    const previousReminderDeactivationReason = useRef<number | undefined>(undefined);
    const previousReminderDeactivationNote = useRef<string>('');

    const getDatePlusReminderDays = useCallback((date: string, forGivenDate: boolean = true) => {
        const dateTime = DateTime.fromISO(date)
        return forGivenDate
            ? dateTime.minus({ days: reminder!.reminderDays }).toISODate()
            : dateTime.plus({ days: reminder!.reminderDays }).toISODate();
    }, [reminder]);

    const reminderDeactivationReasons: ISelectOption[] = configuration.reminderDeactivationReasons.map(({ name, reminderDeactivationReasonId }) => ({ value: reminderDeactivationReasonId, label: name }))
    const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
    const unsavedChangesPrompt = useUnsavedChangesWarning(isDirty);
    const resetProperFields = useCallback(() => {
        resetField("givenDate");
        resetField("dueDate");
        resetField("reminderSourceId");
        resetField("reminderSourceName");
        setSelectedReminderSource(undefined)
    }, [resetField]);

    //Effects used to handle the due date and given date when isEditing is true
    useEffect(() => {
        if (isEditing && dueDateValue) {
            if (reminder?.reminderDays && previousGivenDateRef.current !== givenDateValue) {
                const addedDueDate = getDatePlusReminderDays(givenDateValue, false);
                setValue("dueDate", addedDueDate, { shouldDirty: true });
                reminder.dueDate = addedDueDate;

                setValue("givenDate", givenDateValue);
                reminder!.givenDate = givenDateValue;
            }
            else {
                setValue("givenDate", givenDateValue);
                reminder!.givenDate = givenDateValue;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [givenDateValue, getDatePlusReminderDays, isEditing, reminder, setValue]);

    useEffect(() => {
        if (isEditing && givenDateValue) {
            if (reminder?.reminderDays && previousDueDateRef.current !== dueDateValue) {
                const addedGivenDate = getDatePlusReminderDays(dueDateValue);
                setValue("givenDate", addedGivenDate, { shouldDirty: true });
                reminder.givenDate = addedGivenDate;

                setValue("dueDate", dueDateValue);
                reminder!.dueDate = dueDateValue;
            }
            else {
                setValue("dueDate", dueDateValue);
                reminder!.dueDate = dueDateValue;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getDatePlusReminderDays, dueDateValue, isEditing, reminder, setValue]);


    //Effects used to handle the due date and given date when adding a NEW reminder and has reminder days set
    useEffect(() => {
        setSelectedDate(dueDateValue ? DateTime.fromISO(dueDateValue).toISODate() : '');
        if (selectedReminderSource?.reminderSourceId === ReminderSource.ClientProvided && dueDateValue && previousDueDateRef.current !== dueDateValue && reminder?.reminderDays) {
            const addedGivenDate = getDatePlusReminderDays(dueDateValue);
            if (addedGivenDate !== previousGivenDateRef.current) {
                setValue("givenDate", addedGivenDate, { shouldDirty: true });
                previousGivenDateRef.current = addedGivenDate;
            }
        }
    }, [dueDateValue, getDatePlusReminderDays, reminder?.reminderDays, selectedReminderSource?.reminderSourceId, setValue]);

    useEffect(() => {
        if (selectedReminderSource?.reminderSourceId === ReminderSource.ClientProvided && givenDateValue && previousGivenDateRef.current !== givenDateValue && reminder?.reminderDays) {
            const addedDueDate = getDatePlusReminderDays(givenDateValue, false);
            if (addedDueDate !== previousDueDateRef.current) {
                setValue("dueDate", addedDueDate, { shouldDirty: true });
                previousDueDateRef.current = addedDueDate;
            }
        }
    }, [givenDateValue, getDatePlusReminderDays, reminder?.reminderDays, selectedReminderSource?.reminderSourceId, setValue]);

    const getDateLabel = (reminderSourceId: number) => reminderSourceId === ReminderSource.ClientProvided ? 'Date Given' : 'Date Recommended';

    useEffect(() => {
        if (!reminder) return;

        // Set Due Date
        if (reminder.dueDate) {
            setSelectedDate(DateTime.fromISO(reminder.dueDate).toISODate());
            setValue("dueDate", reminder.dueDate);
            previousDueDateRef.current = reminder.dueDate;
        }

        // Set Given Date
        if (reminder.givenDate) {
            setValue("givenDate", reminder.givenDate);
            previousGivenDateRef.current = reminder.givenDate;
        }

        if (reminder.reminderSourceId) {
            setValue("reminderSourceId", reminder.reminderSourceId);
            setSelectedReminderSource({ reminderSourceId: reminder.reminderSourceId, label: getDateLabel(reminder.reminderSourceId) });
            previousReminderSourceIdRef.current = reminder.reminderSourceId;
            previousReminderNameRef.current = reminder.reminderSourceName;
        }
    }, [reminder, setValue, setSelectedDate]);

    useEffect(() => {
        let valueToSet = '';
        if (!selectedReminderSource || isEditing) return;
        if (selectedReminderSource?.reminderSourceId === ReminderSource.ClientProvided && previousReminderSourceIdRef.current == 1) {
            valueToSet = previousReminderSourceIdRef.current
                ? previousGivenDateRef.current
                : '';

            const dueDateToSet = previousReminderSourceIdRef.current ? previousDueDateRef.current : '';
            setValue("dueDate", dueDateToSet);
        }
        else if (selectedReminderSource?.reminderSourceId === ReminderSource.DoctorRecommended && previousReminderSourceIdRef.current == 2) {
            valueToSet = today.toISODate();

            const dueDateToSet = previousReminderSourceIdRef.current ? previousDueDateRef.current : '';
            setValue("dueDate", dueDateToSet);
        }
        else if (selectedReminderSource?.reminderSourceId !== previousReminderSourceIdRef.current) {
            valueToSet = selectedReminderSource?.reminderSourceId === ReminderSource.ClientProvided ? '' : today.toISODate();
            setValue("dueDate", '');
        }
        if (previousGivenDateRef.current >= dueDateValue) {
            setValue("dueDate", '');
        }
        reminder!.givenDate = valueToSet;
        setValue("givenDate", valueToSet);
        //using the next eslint disable due to today const causing multiple executions of this effect
        //and not even allowing the logic to execute
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reminder, selectedReminderSource?.reminderSourceId, setValue]);

    const resetReminder = () => {
        reminder!.dueDate = '';
        reminder!.givenDate = '';
        reminder!.reminderSourceId = 0;
        setSelectedDate('');
        setSelectedReminderSource(undefined);
    };

    const restorePreviousReminder = () => {
        reminder!.dueDate = previousDueDateRef.current;
        reminder!.givenDate = previousGivenDateRef.current;
        reminder!.reminderSourceId = previousReminderSourceIdRef.current;
        reminder!.reminderSourceName = previousReminderNameRef.current;
        reminder!.isInactive = previousReminderInactive.current;
        reminder!.reminderDeactivationReasonId = previousReminderDeactivationReason.current;
        reminder!.reminderDeactivationNote = previousReminderDeactivationNote.current;
    };

    const onContinue = () => {
        if (isEditing) {
            if (isInactive && updateReminder) {
                reminder!.reminderDeactivationReasonId = reminderDeactivationReason;
                reminder!.reminderDeactivationNote = reminderDeactivationNote;
                updateReminder()
                    .then(() => {
                        store.fetchCustomerInteractions(store.pet.clientId, store.pet.petId)
                    });
            } else {
                updateReminder && updateReminder();
            }
        }
        else {
            reminder!.dueDate = getValues("dueDate");
            reminder!.givenDate = getValues("givenDate");
            reminder!.reminded = true;
            resetProperFields();
            onCancel();
        }
    };

    const closeModal = () => {
        if (isEditing) {
            if (isDirty) {
                setShowCancelConfirm(true);
                return;
            }
            else {
                restorePreviousReminder();
                resetProperFields();
                onCancel();
                return;
            }

        } else if (!reminder?.reminded) {
            resetReminder();
        } else {
            restorePreviousReminder();
        }
        resetProperFields();
        onCancel();
    };

    const getDateValue = useCallback((days: number) => today.plus({ days: days }).toISODate(), [today]);

    const onSelectDateFromButton = (days: number) => {
        const valueToSet = isEditing && reminder 
            ? DateTime.fromISO(reminder.givenDate).plus({ days }).toISODate()
            : getDateValue(days);

        setSelectedDate(valueToSet);
        setValue("dueDate", valueToSet, { shouldDirty: true });
        reminder!.dueDate = valueToSet;
    }

    const reminderOptions: IReminderDaysKeyValuePair[] = [
        {
            displayString: "1 Month(s)",
            reminderDays: ReminderDays.One_Month
        },
        {
            displayString: "3 Month(s)",
            reminderDays: ReminderDays.Three_Months
        },
        {
            displayString: "6 Month(s)",
            reminderDays: ReminderDays.Six_Months
        },
        {
            displayString: "1 Year",
            reminderDays: ReminderDays.Year
        },
    ];

    const getMinDate = (): Date => {
        const dateGiven = getValues("givenDate");
        return (dateGiven ? DateTime.fromISO(dateGiven).plus({ days: 1 }) : DateTime.now()).toJSDate();
    }

    const isReminderOptionActive = (reminderDays: number): boolean => isEditing && reminder 
        ? DateTime.fromISO(reminder.dueDate).diff(DateTime.fromISO(reminder.givenDate), 'days').days === reminderDays
        : getDateValue(reminderDays) === selectedDate;

    return (
        <>
            {unsavedChangesPrompt}
            <YesNoConfirmModal
                visible={showCancelConfirm}
                title="Cancel Changes"
                description="You have unsaved changes, are you sure you want to cancel?"
                busy={false}
                cancelButtonText="No"
                onConfirm={() => {
                    restorePreviousReminder();
                    resetProperFields();
                    setShowCancelConfirm(false);
                    onCancel();
                }}
                onCancel={() => setShowCancelConfirm(false)}
            />

            <Modal className="modal-remindar-duedate-fade" centered size='lg' show={modalVisible} backdrop="static" onHide={closeModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? makeBold(`Update ${reminder?.salesItemName} Reminder`, undefined) : makeBold(reminder?.salesItemName, undefined)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <section className='mt-2'>
                        {
                            // Decided not to use a selectable property or something similar 
                            // because the checked property validation can be done 
                            // using the reminder object passed into this component
                            // and onChange always updates the reminderSourceId
                            // which is what's needed for comparison
                            configuration.reminderSources.map((rs: ReminderSourceModel, index: number) =>
                                <Controller
                                    name={`reminderSourceId`}
                                    control={control}
                                    key={`reminderSourceId_${index}`}
                                    render={({ field: { onChange, name } }) => {
                                        return <Form.Check
                                            label={rs.name}
                                            type="radio"
                                            id={`radio-${rs.name}`}
                                            name={name}
                                            disabled={isEditing}
                                            data-testid={`reminderSourceId_${index}`}
                                            className='third-party-reminder-checkbox'
                                            checked={reminder!.reminderSourceId == rs.reminderSourceId}
                                            value={rs.reminderSourceId}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setValue("reminderSourceId", rs.reminderSourceId);
                                                    setSelectedReminderSource({
                                                        reminderSourceId: rs.reminderSourceId,
                                                        label: getDateLabel(rs.reminderSourceId)
                                                    } as IReminderSourceDateLabel);
                                                    reminder!.reminderSourceId = rs.reminderSourceId;
                                                    reminder!.reminderSourceName = rs.name;
                                                    onChange(e);
                                                }
                                            }} />
                                    }}
                                />
                            )
                        }
                    </section>
                    {
                        selectedReminderSource === undefined
                            ? undefined
                            : <>
                                <Col className='pb-2'>
                               
                                    <FormInput
                                        type='date'
                                        label={`${selectedReminderSource.label}:`}
                                        fieldName='givenDate'
                                        control={control}
                                        portalId="root-portal"
                                        maxDate={selectedReminderSource.reminderSourceId == ReminderSource.ClientProvided ? DateTime.now().setZone(timeZone).toJSDate() : undefined}
                                        readOnly={isEditing ? false : selectedReminderSource.reminderSourceId === ReminderSource.DoctorRecommended}
                                        isClearable={selectedReminderSource.reminderSourceId === ReminderSource.ClientProvided}
                                    />
                                </Col>
                                <Col className='pb-2'>
                                    <FormInput
                                        type='date'
                                        label='Due Date:'
                                        fieldName='dueDate'
                                        control={control}
                                        portalId="root-portal"
                                        minDate={getMinDate()}
                                    />
                                </Col>
                            </>
                    }
                    {
                        selectedReminderSource?.reminderSourceId === ReminderSource.DoctorRecommended &&
                        <section className='d-flex gap-1 pt-3'>
                            {
                                reminderOptions.map(reminderOption => (
                                    <ToggleButton
                                        key={reminderOption.reminderDays}
                                        variant='outline-primary'
                                        onClick={() => onSelectDateFromButton(reminderOption.reminderDays)}
                                        active={isReminderOptionActive(reminderOption.reminderDays)}
                                        value={getDateValue(reminderOption.reminderDays)}
                                    >
                                        {reminderOption.displayString}
                                    </ToggleButton>
                                ))
                            }
                        </section>
                    }
                    {
                        isEditing &&
                        <>
                            <div className="pt-4">
                                <FormInput
                                    type="checkbox"
                                    label="Mark as inactive"
                                    fieldName="isInactive"
                                    className="ms-0"
                                    control={control}
                                    isRequired={false}
                                />
                            </div>
                        </>
                    }
                    {
                        isInactive &&
                        <>
                            <div className="pt-4">
                                <FormSelect label='Reason' fieldName="reminderDeactivationReasonId" control={control}
                                    options={reminderDeactivationReasons}
                                    getOptionLabel={(r) => r.label}
                                    getOptionValue={(r) => r.value.toString()}
                                    mapValueToOption={value => _.find(reminderDeactivationReasons, { value })}
                                    mapOptionToValue={opt => opt?.value}
                                    highlightDirty={true}
                                    isRequired={true}
                                />
                            </div>
                            <div>
                                <FormInput label="Notes" fieldName="reminderDeactivationNote" type="textarea" control={control} placeholder="Type your notes here" />
                            </div>
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <SuspenseButton
                        variant='primary'
                        disabled={!dueDateValue || !givenDateValue || !reminderSourceIdValue || (isInactive && !reminderDeactivationReason)}
                        onClick={onContinue}
                        hideSpinner={false}
                        tag='updatePetcustomReminder'>
                        {isEditing ? 'Save' : 'Continue'}
                    </SuspenseButton>
                </Modal.Footer>
            </Modal>
        </>
    )
});
